import type { SchedulerEmbedProvider } from "../types";
import { loadWidgetScript } from "../utils";

function buildPrefilledEmbedUrl(
  base: string,
  name: string | undefined | null,
  email: string | undefined | null,
  hideEventTypeDetails: boolean
): string {
  const params: string[] = [];
  if (typeof window !== "undefined") {
    params.push(`embed_domain=${encodeURIComponent(window.location.host)}`);
    params.push("embed_type=Inline");
  }
  if (name) params.push(`name=${encodeURIComponent(name)}`);
  if (email) params.push(`email=${encodeURIComponent(email)}`);
  if (hideEventTypeDetails) params.push("hide_event_type_details=1");
  params.push("hide_gdpr_banner=1");
  return params.length > 0 ? `${base}?${params.join("&")}` : base;
}

export const calendlyEmbedProvider: SchedulerEmbedProvider = {
  id: "CALENDLY",
  label: "Calendly",
  syncEmbedToPanelHeight: true,

  mount(ctx) {
    let cancelled = false;
    const slot = ctx.container;
    slot.innerHTML = "";

    const widget = document.createElement("div");
    widget.className = "calendly-inline-widget";
    const prefilledUrl = buildPrefilledEmbedUrl(
      ctx.embedConfig.embedUrl,
      ctx.recalledName,
      ctx.recalledEmail,
      true
    );
    widget.setAttribute("data-url", prefilledUrl);
    widget.setAttribute("data-resize", "true");
    slot.appendChild(widget);

    loadWidgetScript(ctx.embedConfig.widgetScriptUrl)
      .then(() => {
        if (cancelled) return;
        const calendly = (
          window as unknown as {
            Calendly?: {
              initInlineWidget?: (opts: {
                url: string;
                parentElement: HTMLElement;
                resize?: boolean;
              }) => void;
            };
          }
        ).Calendly;
        if (calendly?.initInlineWidget) {
          widget.innerHTML = "";
          calendly.initInlineWidget({
            url: prefilledUrl,
            parentElement: widget,
            resize: true,
          });
        }
      })
      .catch((e: Error) => {
        if (!cancelled) ctx.onError(e.message);
      });

    return () => {
      cancelled = true;
      slot.innerHTML = "";
    };
  },

  useReadyListener({ widgetMounted, booking, onReady }) {
    if (!widgetMounted || booking) return;
    let cancelled = false;
    let revealTimer: number | undefined;
    let stabilizeTimer: number | undefined;
    let fallbackTimer: number | undefined;
    let sawViewed = false;
    let maxPageHeight = 0;

    const parsePageHeight = (payload: unknown): number => {
      if (typeof payload !== "object" || payload === null) return 0;
      const heightRaw = (payload as Record<string, unknown>)["height"];
      if (typeof heightRaw === "string") {
        return Number.parseInt(heightRaw.replace("px", ""), 10) || 0;
      }
      return typeof heightRaw === "number" ? heightRaw : 0;
    };

    const markReady = () => {
      if (cancelled) return;
      revealTimer = window.setTimeout(() => {
        if (!cancelled) onReady();
      }, 80);
    };

    const scheduleReadyAfterStableHeight = () => {
      if (!sawViewed || maxPageHeight < 400) return;
      if (stabilizeTimer !== undefined) window.clearTimeout(stabilizeTimer);
      stabilizeTimer = window.setTimeout(() => {
        if (!cancelled) markReady();
      }, 300);
    };

    const onMessage = (event: MessageEvent) => {
      if (!event.origin?.endsWith("calendly.com")) return;
      const data = event.data;
      if (typeof data !== "object" || data === null) return;
      const eventName = (data as Record<string, unknown>)["event"];
      const payload = (data as Record<string, unknown>)["payload"];

      if (
        eventName === "calendly.event_type_viewed" ||
        eventName === "calendly.profile_page_viewed"
      ) {
        sawViewed = true;
        scheduleReadyAfterStableHeight();
        return;
      }

      if (eventName === "calendly.page_height") {
        maxPageHeight = Math.max(maxPageHeight, parsePageHeight(payload));
        scheduleReadyAfterStableHeight();
      }
    };

    window.addEventListener("message", onMessage);
    fallbackTimer = window.setTimeout(markReady, 8_000);

    return () => {
      cancelled = true;
      window.removeEventListener("message", onMessage);
      if (revealTimer !== undefined) window.clearTimeout(revealTimer);
      if (stabilizeTimer !== undefined) window.clearTimeout(stabilizeTimer);
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
    };
  },

  useSizeSync({ embedFrame, container, embedContentReady, booking }) {
    if (!embedContentReady || booking) return;

    const syncWidgetSize = () => {
      const height = embedFrame.clientHeight;
      if (height <= 0) return;
      const widget = embedFrame.querySelector<HTMLElement>(".calendly-inline-widget");
      const iframe = embedFrame.querySelector<HTMLIFrameElement>(".calendly-inline-widget iframe");
      if (widget) {
        widget.style.setProperty("height", `${height}px`, "important");
        widget.style.setProperty("min-height", `${height}px`, "important");
        widget.style.setProperty("width", "100%", "important");
      }
      if (iframe) {
        iframe.style.setProperty("height", `${height}px`, "important");
        iframe.style.setProperty("min-height", `${height}px`, "important");
        iframe.style.setProperty("width", "100%", "important");
      }
    };

    syncWidgetSize();
    const resizeObserver = new ResizeObserver(syncWidgetSize);
    resizeObserver.observe(embedFrame);

    const mutationObserver = new MutationObserver(syncWidgetSize);
    if (container) {
      mutationObserver.observe(container, {
        attributes: true,
        subtree: true,
        attributeFilter: ["style"],
      });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  },
};

interface CalendlyScheduledPayload {
  event: { uri: string };
  invitee: { uri: string };
}

export function isCalendlyScheduledEvent(
  data: unknown
): data is { event: string; payload: CalendlyScheduledPayload } {
  if (typeof data !== "object" || data === null) return false;
  const m = data as Record<string, unknown>;
  if (m["event"] !== "calendly.event_scheduled") return false;
  const payload = m["payload"];
  if (typeof payload !== "object" || payload === null) return false;
  const p = payload as Record<string, unknown>;
  const event = p["event"] as Record<string, unknown> | undefined;
  const invitee = p["invitee"] as Record<string, unknown> | undefined;
  return (
    !!event &&
    typeof event["uri"] === "string" &&
    !!invitee &&
    typeof invitee["uri"] === "string"
  );
}
